import type { BaseElement, BaseText } from 'slate'
import type { RenderElementProps, RenderLeafProps } from 'slate-react'

export const H_TAGS = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6']

// slate剪切板片段的内置格式类型
export const SLATE_FRAGMENT_TRANSFER_KEY = 'application/x-slate-fragment'
// 自定义slate剪切板片段的格式类型
export const CUSTOM_SLATE_FRAGMENT_TRANSFER_KEY = 'application/x-slate-fragment-custom'
// 用来标识自定义的Slate节点拖拽的数据类型
export const CUSTOM_SLATE_DATA_TRANSFER_KEY = 'application/x-slate-fragment-custom-node'

// 自定义节点属性: 不需要aime那边处理query(海外底层页里面的业务逻辑)
export const DATA_NO_AIME = 'data-no-aime'

// 自定义元素类型
export enum CustomElementType {
  // 默认元素类型
  paragraph = 'paragraph',
  numberedList = 'numbered-list',
  blockquote = 'blockquote',
  pre = 'pre',
  horizontalRule = 'hr',
  h1 = 'h1',
  h2 = 'h2',
  h3 = 'h3',
  h4 = 'h4',
  h5 = 'h5',
  h6 = 'h6',
  a = 'a',
  span = 'span',
  richTable = 'rich-table', // 富文本表格
  richTableHead = 'thead', // 富文本表格head
  richTableBody = 'tbody', // 富文本表格body
  richTableFoot = 'tfoot', // 富文本表格foot
  richTableRow = 'tr', // 富文本表格row
  richTableCell = 'td', // 富文本表格cell
  richTableHeadCell = 'th', // 富文本表格head cell`
  // 自定义元素类型(本地嵌套)
  Image = 'image',
  Bulleted = 'bulleted-list',
  ListItem = 'list-item',
  stock = 'stock',
  visualization = 'visualization',
  htmlVisualization = 'html-visualization',
  text2img = 'text2img', // 文本prompt转图片
  text2visual = 'text2visual', // 文本prompt转可视化图表
  video = 'video',
  backtest = 'backtest-component',
  embeddedComponent = 'embedded-component',
  cardInfos = 'card-infos'
}

// 列表类型
export const LIST_TYPES = ['numbered-list', 'bulleted-list']
// 文本对齐方式
export const TEXT_ALIGN_TYPES = ['left', 'center', 'right', 'justify']

export interface CustomElement extends BaseElement {
  [x: string]: any
  type?: CustomElementType
}

export interface IRenderElementProps extends RenderElementProps {
  element: CustomElement
}

// marks
export enum CustomMarkType {
  Bold = 'bold',
  Italic = 'italic',
  Underline = 'underline',
  fontSize = 'fontSize',
  commentHighlight = 'commentHighlight',
  commentId = 'commentId',
  active = 'active',
  chineseHighlight = 'chineseHighlight'
}

export interface CustomText extends BaseText {
  code?: boolean
  [CustomMarkType.Bold]?: boolean
  [CustomMarkType.Italic]?: boolean
  [CustomMarkType.Underline]?: boolean
  [CustomMarkType.fontSize]?: string
  [CustomMarkType.commentHighlight]?: string
  [CustomMarkType.commentId]?: string
  [CustomMarkType.active]?: boolean
  [CustomMarkType.chineseHighlight]?: boolean
}

export interface IRenderLeafProps extends RenderLeafProps {
  leaf: CustomText
}

export interface CustomImageElemet extends CustomElement {
  id: string
  src?: string // 如果需要单独上传图片，此字段不生效
  href?: string
  aiGCInfo?: {
    // AI生成的图片的一些信息
    isAIGC: boolean // 是否是ai生成的图片
    prompt: string
  }
  uploadInfo?: {
    // 上传图片信息， 需要Image组件内部单独处理，此时src字段不生效
    needUpload: boolean // 是否需要单独上传
    // 下载地址(一般是外部粘贴进来的内容经过处理得到，先根据url下载图片，然后上传到服务器)
    downloadUrl?: string
    // base64数据(一般是外部粘贴进来的内容经过处理得到，直接上传到服务器)
    base64Data?: string
    file?: File // 上传的文件（一般是拖拽上传的文件）
  }
}

// 文本prompt转图片的信息（对应正文中的文生图）
export interface Text2ImgInfo {
  text: string
  start: boolean // 特殊标签开始标志,开始后，后面的数据对应的值都是null
  ongoing: boolean // 在start为true时，转变成true，finish为true时，转变成false
  finish: boolean // 特殊标签结束标志，只有结束时为true，其他阶段为null
}

// 文生图元素（过程元素，结束后会转为图片）
export interface CustomText2ImgElemet extends CustomElement {
  id: string
  data: Text2ImgInfo
}

// 文本prompt转可视化图表的信息（对应正文中的可视化图表）
export interface Text2VisualInfo {
  text: string
  start: boolean // 特殊标签开始标志,开始后，后面的数据对应的值都是null
  ongoing: boolean // 在start为true时，转变成true，finish为true时，转变成false
  finish: boolean // 特殊标签结束标志，只有结束时为true，其他阶段为null
}

// 文生可视化元素（过程元素，结束后会转为可视化组件来展示）
export interface CustomText2VisualElemet extends CustomElement {
  id?: string
  data: Text2VisualInfo
}

// 富文本表格元素
export interface CustomTableElement extends CustomElement {
  data: {
    id: string
    columns: CustomCoumnData[]
    tableWidth: number
  }
}

export interface CustomCoumnData {
  id: string
  // 列对应的<col>上的宽度
  width: number
}

// 自定义视频元素
export interface CustomVideoElement extends CustomElement {
  data: {
    id: string
    videoUrl: string
    coverUrl?: string
  }
}
